/**
 * Cost per Claim's half of the review comments drawer. The drawer itself is the shared
 * @focalpointcare/review-drawer (fcc-review drawer/); this is what only this app knows:
 *
 *  - how to call the API: apiFetch with the ID token, plus the view-as header, so the
 *    API refuses (403) anything that slips past the UI while viewing as someone;
 *  - when to ask at all: signed in, Dev mode showing comments, not viewing as someone;
 *  - the view: a comment keeps it portably (people as keys; viewContext.ts), reads its
 *    older comments' filter bar too, and opens either within the reader's own access;
 *  - who a picked figure is about: the row's person, the drill-through it is in, or the
 *    one person the Employee view shows; the month from its column or a one-month range;
 *  - the Review Hub link, month names, and this app's side panel.
 */
import { useMemo, useSyncExternalStore } from 'react'
import type { ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  ReviewProvider,
  type FigureRef,
  type PanelProps,
  type ReviewHost,
  type Subject,
  type Thread,
  type ViewNow,
} from '@focalpointcare/review-drawer'
import { useAuth } from '@/auth/AuthProvider'
import { apiFetch, TOKEN_HEADER } from '@/auth/apiFetch'
import { PORTAL_URL } from '@/components/PortalBar'
import { SidePanel } from '@/components/SidePanel'
import { useAppData } from '@/data/AppData'
import { useDataset } from '@/data/DatasetProvider'
import {
  ALL,
  EMPTY_FILTERS,
  FILTER_LABELS,
  PAYROLL_BANDS,
  TENURE_BANDS,
  useFilters,
  type Filters,
  type MultiKey,
} from '@/data/filters'
import { viewingAs, withViewAs } from '@/data/viewAs'
import { usePageLimit } from '@/access/pageLimit'
import { monthYear } from '@/format'
import { peopleKeys } from '@/state/personKeys'
import { captureView } from '@/state/sharedView'
import { captureHere, useOpenSharedView } from '@/state/ViewLinks'
import { shownTools, useDevMode } from '@/ui/devMode'
import { contextFilters, newContext, sameView, viewOfContext, type CpcView } from './viewContext'

const SINGLE_LABELS: Partial<Record<keyof Filters, string>> = {
  supervisor: 'Supervisor',
  employee: 'Employee',
  management: 'Management',
  employmentStatus: 'Employment status',
  tenure: 'Tenure',
  payrollBand: 'Payroll',
  monthFrom: 'From',
  monthTo: 'To',
}

/** Filters that differ from the defaults, as "Label: value" strings. */
export function filterSummary(f: Partial<Filters> | undefined): string[] {
  if (!f) return []
  const out: string[] = []
  for (const k of Object.keys(EMPTY_FILTERS) as (keyof Filters)[]) {
    const v = f[k]
    if (v === undefined || JSON.stringify(v) === JSON.stringify(EMPTY_FILTERS[k])) continue
    if (k === 'slice') out.push(`Slice: ${(v as Filters['slice'])?.label ?? ''}`)
    else if (Array.isArray(v)) out.push(`${FILTER_LABELS[k as MultiKey] ?? k}: ${v.join(', ')}`)
    else if (k === 'tenure') out.push(`Tenure: ${TENURE_BANDS[Number(v)]?.label ?? v}`)
    else if (k === 'payrollBand') out.push(`Payroll: ${PAYROLL_BANDS[Number(v)]?.label ?? v}`)
    else out.push(`${SINGLE_LABELS[k] ?? k}: ${String(v)}`)
  }
  return out
}

/* What a review comment calls this page: AppShell knows (its page list), and says so. */
let pageLabel = ''
const listeners = new Set<() => void>()
export function setPageLabel(label: string) {
  if (label === pageLabel) return
  pageLabel = label
  listeners.forEach((l) => l())
}
const usePageLabel = () =>
  useSyncExternalStore(
    (l) => (listeners.add(l), () => listeners.delete(l)),
    () => pageLabel,
  )

/** Pages that moved: a thread written on the old address belongs to the new one. */
const MOVED: Record<string, string> = {
  // My team's metrics became My metrics' team view (review #59, 2026-10-05).
  '/supervisor-view': '/employee-view',
}

function useCurrent(): ViewNow<CpcView> {
  const { pathname } = useLocation()
  const { filters } = useFilters()
  const { dataset } = useDataset()
  const label = usePageLabel()
  const version = dataset?.version ?? null
  return useMemo(
    () => ({
      path: pathname,
      label: label || pathname,
      datasetVersion: version,
      summary: filterSummary(filters),
      // The view, portable: people as keys, a slice as its bar. The data version is the drawer's to add.
      capture: () => {
        const { datasetVersion: _, ...fields } = newContext(captureHere(), null)
        return fields
      },
    }),
    [pathname, label, version, filters],
  )
}

function useOpen() {
  const navigate = useNavigate()
  const openShared = useOpenSharedView()
  return (t: Thread<CpcView>) => {
    if (!t.pagePath) return
    // Old and new contexts alike, within the reader's access (viewContext.ts).
    if (t.context) {
      const { view, refused } = viewOfContext(t.pagePath, t.context, peopleKeys())
      openShared(view, refused)
    } else navigate(t.pagePath)
  }
}

/** A figure from a drill-through is not on the bare page: reopen that person. */
function useReveal() {
  const { openPerson } = useAppData()
  return (t: Thread<CpcView>) => {
    if (!t.subjectName) return false
    openPerson(t.subjectName)
    return true
  }
}

function useIsHere() {
  const location = useLocation()
  return (t: Thread<CpcView>) => {
    if (!t.pagePath || !t.context) return false
    const keys = peopleKeys()
    return sameView(captureView(location, keys), viewOfContext(t.pagePath, t.context, keys).view)
  }
}

/**
 * Who a picked figure is about, so it also shows in that person's drill-through and on
 * their row badge: the row's key when it names someone, else the drill-through the click
 * landed in, else the one person the Employee view is showing. The month: the column,
 * when its header is a month, else a one-month range.
 */
function useSubject() {
  const { derived } = useAppData()
  const { filters } = useFilters()
  const { pathname } = useLocation()
  return useMemo(() => {
    const people = new Set(derived.people.map((p) => p.employee))
    const monthByLabel = new Map<string, string>()
    for (const m of derived.months) {
      monthByLabel.set(monthYear(m), m)
      monthByLabel.set(monthYear(m).split(' ')[0]!, m)
      monthByLabel.set(m, m)
    }
    return (el: Element, figure: FigureRef): Subject | null => {
      const rowPerson = figure.table?.row && people.has(figure.table.row) ? figure.table.row : null
      const dialog = el.closest('[role="dialog"]')?.getAttribute('aria-label') ?? null
      const panelPerson = dialog && people.has(dialog) ? dialog : null
      const pagePerson = pathname === '/employee-view' && filters.employee !== ALL ? filters.employee : null
      const name = rowPerson ?? panelPerson ?? pagePerson
      if (!name) return null
      const month =
        (figure.table?.column ? monthByLabel.get(figure.table.column) : undefined) ??
        (filters.monthFrom !== ALL && filters.monthFrom === filters.monthTo ? filters.monthFrom : null)
      return { name, month }
    }
  }, [derived, filters, pathname])
}

/** This app's side panel, above a drill-through it was opened from, keeping a popped-out chart. */
function Panel({ onClose, label, width, zIndex, hidden, children }: PanelProps) {
  return (
    <SidePanel onClose={onClose} label={label} width={width} zIndex={zIndex} hidden={hidden} keepsPopOut>
      {children}
    </SidePanel>
  )
}

export const reviewHost: ReviewHost<CpcView> = {
  // apiFetch swaps in the current ID token for this placeholder (auth/apiFetch.ts).
  fetch: (path, init) =>
    apiFetch(path, { ...init, headers: withViewAs({ ...(init.headers as Record<string, string>), [TOKEN_HEADER]: 'id-token' }) }),
  app: { id: 'cpc', name: 'Cost per Claim' },
  // The Review Hub in the FCC Portal, with a way back (fcc-portal backLink follows it
  // only on this app's own site). `cpc` is this app's REVIEW_APP.
  hubHref: () => (PORTAL_URL ? `${PORTAL_URL}/hub?from=cpc&back=${encodeURIComponent(window.location.href)}` : null),
  note: 'In development. Only the reviewers on the list can see these.',
  formatMonth: monthYear,
  view: {
    useCurrent,
    useOpen,
    useReveal,
    useIsHere,
    summary: (t) => filterSummary(t.pagePath ? (contextFilters(t.pagePath, t.context, peopleKeys()) ?? undefined) : undefined),
    samePage: (a, b) => (MOVED[a] ?? a) === (MOVED[b] ?? b),
  },
  figures: { useSubject },
  shell: { Panel },
}

/**
 * Reviewers see comments only with Dev mode on (ui/devMode.tsx), so with it off the
 * app looks as everyone else sees it. And never while viewing as someone: View as is
 * read-only, and the API refuses these requests too (api/shared/viewAs.ts). Nor for a
 * page-limited reader, or while viewing as one: they are not reviewers, and the API
 * refuses them too (api/shared/pages.ts).
 *
 * Still asking (signed in, but Dev mode's settings not loaded yet) is null, not false:
 * false closes the drawer, which a ?comment= link has just opened.
 */
export function CommentsProvider({ children }: { children: ReactNode }) {
  const { ready } = useAuth()
  const dev = useDevMode()
  const limited = usePageLimit() !== null
  const active =
    viewingAs() || limited ? false : !ready || dev.features === null ? null : shownTools(dev.features, dev.on).comments
  return (
    <ReviewProvider host={reviewHost} active={active}>
      {children}
    </ReviewProvider>
  )
}
